import { Controller } from "@hotwired/stimulus";

// Live feedback for an Employer Name Do Not Contact entry: shows the normalized form the value is
// stored and compared in, and how many open verifications saving it would close. Normalizing and
// counting both happen server-side so the preview can't drift from the gate.
//
// Usage (on the <form>):
//   data-controller="support--dnc-company-preview"
//   data-support--dnc-company-preview-url-value="<preview path>"
//   data-support--dnc-company-preview-company-type-value="COMPANY_NAME"
export default class extends Controller {
  static targets = ["type", "value", "scope", "panel", "normalized", "closing"];
  static values = { url: String, companyType: String };

  connect() {
    this.toggle();
  }

  disconnect() {
    clearTimeout(this.timer);
    this.request?.abort();
  }

  get active() {
    return this.typeTarget.value === this.companyTypeValue;
  }

  toggle() {
    this.panelTarget.classList.toggle("d-none", !this.active);
    if (!this.active) {
      // A preview still in flight would otherwise land after this and re-show a warning on an
      // Email or Domain entry, which closes nothing.
      this.request?.abort();
      this.renderClosing(0);
    }
    this.refresh();
  }

  refresh() {
    clearTimeout(this.timer);
    if (!this.active) return;

    this.timer = setTimeout(() => this.fetchPreview(), 250);
  }

  async fetchPreview() {
    this.request?.abort();
    this.request = new AbortController();

    const params = new URLSearchParams({ value: this.valueTarget.value, company_id: this.scopeTarget.value });

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      });
      if (!response.ok) throw new Error(`Preview failed: ${response.status}`);

      // An expired session redirects to the sign-in page, so json() throws here too.
      const preview = await response.json();
      this.normalizedTarget.value = preview.normalized;
      this.renderClosing(preview.closing_count);
    } catch (error) {
      if (error.name === "AbortError") return;

      // A hidden warning reads as "closes nothing", so an unknown count must say so: the count is
      // the only signal before an irreversible close.
      this.renderClosing(null);
    }
  }

  // count: a number from the server, or null when it couldn't be checked.
  renderClosing(count) {
    this.closingTarget.classList.toggle("d-none", count === 0);
    if (count === null) {
      this.closingTarget.textContent =
        "Couldn't check how many open verifications this entry would close. Refresh the page before saving.";
    } else if (count > 0) {
      this.closingTarget.textContent =
        `Saving this entry will close ${count} open verification${count === 1 ? "" : "s"} at matching ` +
        "employers. Deleting the entry later won't reopen them.";
    } else {
      this.closingTarget.textContent = "";
    }
  }
}
